import { Rototilt } from "../interfaces/api/Rototilt";
import { SwitchViewMode } from "../shared/enums/SwitchViewMode";
import { castHelper } from "../shared/helpers/castHelper";
import { filtering, localization } from "../shared/helpers/productFilterHelper";
import { useMachineTonnage } from "../store/hooks/useMachineTonnage";
import { useMarket } from "../store/hooks/useMarket";
import { MachineTonnage } from "../store/interfaces/MachineTonnage";
import { ProductData } from "../store/interfaces/productData";

interface FilterTiltrotatorsProps {
  tiltrotatorsData: ProductData;
}

interface FilterTiltrotatorsOutput {
  filteredTiltrotators?: Rototilt[];
  tiltrotatorsMaxWeight?: number;
  tiltrotatorsMinWeight?: number;
}

interface MachineTonnageWeight {
  maxMachineTonnageWeight: number;
  minMachineTonnageWeight: number;
}

interface TiltrotatorsMaxWeight {
  tiltrotatorsMaxWeight: number;
  tiltrotatorsMinWeight: number;
}

const filterFamilyNotClassicLine = (rototilt: Rototilt) => {
  return rototilt.family !== "Classic Line";
};

const filterRototilts = (
  rototilts: Rototilt[],
  showAll: boolean,
  market: string
): Rototilt[] => {
  if (!rototilts) {
    return [];
  }

  const filteredRototilts = rototilts
    .filter(filterFamilyNotClassicLine)
    .filter((item) => (!showAll ? filtering.market(item, market) : item));

  return filteredRototilts;
};

const getWeightsFromMachineTonnage = (
  machineTonnage: MachineTonnage,
  market: string
): MachineTonnageWeight => {
  if (machineTonnage.switchViewMode === SwitchViewMode.MachineSearch) {
    const maxMachineTonnageWeight = localization.value(
      machineTonnage.machineModel.maxMachineWeight,
      market
    );
    const minMachineTonnageWeight = localization.value(
      machineTonnage.machineModel.minMachineWeight,
      market
    );

    return { minMachineTonnageWeight, maxMachineTonnageWeight };
  }

  return {
    minMachineTonnageWeight: castHelper.toKilogram(machineTonnage.tonnage),
    maxMachineTonnageWeight: castHelper.toKilogram(machineTonnage.tonnage),
  };
};

const getMinMaxMachineWeight = (
  filteredTiltrotators: Rototilt[],
  market: string
): TiltrotatorsMaxWeight => {
  const largeNumber = 99999999999999;

  const maxMachineWeight = filteredTiltrotators.reduce((max, current) => {
    const currentWeight = Number(
      localization.value(current.maximumMachineWeight, market)
    );

    return currentWeight > max ? currentWeight : max;
  }, 0);

  const minMachineWeight = filteredTiltrotators.reduce((min, current) => {
    const currentWeight = Number(
      localization.value(current.minimumMachineWeight, market)
    );

    return currentWeight <= min ? currentWeight : min;
  }, largeNumber);

  return {
    tiltrotatorsMaxWeight: maxMachineWeight
      ? castHelper.toTon(maxMachineWeight)
      : 0,
    tiltrotatorsMinWeight:
      minMachineWeight && minMachineWeight !== largeNumber
        ? castHelper.toTon(minMachineWeight)
        : 0,
  };
};

export const useFilterTiltrotators = ({
  tiltrotatorsData,
}: FilterTiltrotatorsProps): FilterTiltrotatorsOutput => {
  const market = useMarket();
  const machineTonnage = useMachineTonnage();

  if (!tiltrotatorsData || !machineTonnage) {
    return {
      filteredTiltrotators: [],
      tiltrotatorsMaxWeight: 0,
      tiltrotatorsMinWeight: 0,
    };
  }

  const filteredRototilts = filterRototilts(
    tiltrotatorsData.data as Rototilt[],
    tiltrotatorsData.showAll,
    market
  );

  const filteredProducts = filteredRototilts.filter((rototilt) => {
    if (
      !localization.value(rototilt.minimumMachineWeight, market) ||
      !localization.value(rototilt.maximumMachineWeight, market)
    ) {
      return rototilt;
    }

    const maxTiltrotatorWeight = Number(
      localization.value(rototilt.maximumMachineWeight, market)
    );
    const minTiltrotatorWeight = Number(
      localization.value(rototilt.minimumMachineWeight, market)
    );

    const { maxMachineTonnageWeight, minMachineTonnageWeight } =
      getWeightsFromMachineTonnage(machineTonnage, market);

    //Tonnage (slider) values
    const totalMachineTonnageWeight =
      minMachineTonnageWeight + maxMachineTonnageWeight;
    const diffMachineTonnageWeight =
      maxMachineTonnageWeight - minMachineTonnageWeight;
    const totalDiffMachineTonnageWeight =
      totalMachineTonnageWeight - diffMachineTonnageWeight;

    //Tiltrotator (machine search) value
    const totalTiltrotatorWeight = minTiltrotatorWeight + maxTiltrotatorWeight;
    const diffTiltrotatorWeight = maxTiltrotatorWeight - minTiltrotatorWeight;
    const totalDiffTiltrotatorWeight =
      totalTiltrotatorWeight + diffTiltrotatorWeight;

    return (
      totalDiffTiltrotatorWeight >= totalDiffMachineTonnageWeight &&
      maxMachineTonnageWeight >= minTiltrotatorWeight
    );
  });

  const filteredTiltrotators = filteredProducts || filteredRototilts;
  const { tiltrotatorsMaxWeight, tiltrotatorsMinWeight } =
    getMinMaxMachineWeight(filteredTiltrotators, market);

  return {
    filteredTiltrotators,
    tiltrotatorsMaxWeight,
    tiltrotatorsMinWeight,
  };
};
